import { computed, signal } from "@preact/signals";
import { TWISTERS } from "../content/twisters";
import type { Lang } from "../content/types";
import { isMastered } from "../engine/mastery";
import { rankFor } from "../engine/rank";
import type { PlaqueContext } from "../engine/plaques";
import { sweepPlaques } from "../engine/plaques";
import { clarityReward, rewardFromPlaque, rewardFromRankChange, rewardFromStreakEvent, type Reward } from "../engine/rewards";
import { gradeCard, newCard } from "../engine/srs";
import type { SessionItem } from "../engine/session";
import { pauseTraining, previewStreak, touchDay, weeklyClosedCount } from "../engine/streak";
import { dayStr } from "../lib/date";
import { setThemePref } from "../lib/theme";
import { persistDebounced } from "./persist";
import { buildDemoProfile } from "../dev/demoProfile";
import { defaultProfile, loadProfile, saveProfile, type Grade, type Profile, type ThemeSetting } from "./profile";

function initialProfile(): Profile {
  // dev-only fixture for visually checking screens without grinding out real usage
  if (typeof location !== "undefined" && new URLSearchParams(location.search).get("seed") === "demo") {
    return buildDemoProfile();
  }
  return loadProfile();
}

export const profile = signal<Profile>(initialProfile());

// keep the (currently independent) theme module in sync with the persisted setting on boot
setThemePref(profile.value.settings.theme);

function commit(next: Profile): void {
  profile.value = next;
  persistDebounced(next);
}

const TWISTER_PHONEMES = new Map(TWISTERS.map((t) => [t.id, t.phonemes]));

export type GradeCardResult = { wasNew: boolean; justMastered: boolean; card: Profile["cards"][string] };

/** Grades one card mid-session and persists immediately — so a card graded
    just before someone exits the Booth still counts as real practice. */
export function gradeSessionCard(cardId: string, grade: Grade): GradeCardResult {
  const today = dayStr();
  const before = profile.value.cards[cardId];
  const wasNew = !before;
  const wasMasteredBefore = isMastered(before);
  const graded = gradeCard(before ?? newCard(cardId, today), grade, today);

  const phonemes = TWISTER_PHONEMES.get(cardId) ?? [];
  const nextPhonemes = { ...profile.value.phonemes };
  for (const ph of phonemes) {
    const p = nextPhonemes[ph] ?? { xp: 0, attempts: 0, clean: 0 };
    nextPhonemes[ph] = { xp: p.xp, attempts: p.attempts + 1, clean: p.clean + (grade >= 2 ? 1 : 0) };
  }

  commit({
    ...profile.value,
    cards: { ...profile.value.cards, [cardId]: graded },
    phonemes: nextPhonemes,
  });

  return { wasNew, justMastered: !wasMasteredBefore && isMastered(graded), card: graded };
}

const SESSION_PROGRESS_KEY = "dictionDojo.sessionProgress";

export type SessionProgress = {
  day: string;
  type: string;
  itemIndex: number;
  clarityEarned: number;
  dailyCardId: string | null;
  /** the exact item list from when the session started — resuming replays
      this snapshot rather than recomputing buildSession(), since grading
      cards mid-session shifts what's due and would otherwise reshuffle
      the remaining items out from under the saved index */
  items: SessionItem[];
};

export function saveSessionProgress(p: SessionProgress): void {
  try {
    localStorage.setItem(SESSION_PROGRESS_KEY, JSON.stringify(p));
  } catch {
    /* best-effort — losing the resume marker just means starting over */
  }
}

export function loadSessionProgress(): SessionProgress | null {
  try {
    const raw = localStorage.getItem(SESSION_PROGRESS_KEY);
    return raw ? (JSON.parse(raw) as SessionProgress) : null;
  } catch {
    return null;
  }
}

export function clearSessionProgress(): void {
  try {
    localStorage.removeItem(SESSION_PROGRESS_KEY);
  } catch {
    /* nothing to clean up */
  }
}

/** Records a new breath-count personal best, if `count` beats it. Returns
    whether it actually was a new PB. */
export function recordBreathPB(count: number): boolean {
  if (count <= profile.value.bests.breathCount) return false;
  commit({ ...profile.value, bests: { ...profile.value.bests, breathCount: count } });
  return true;
}

/** Records a new max-phonation-time PB (mic-derived seconds of continuous
    sustained voicing), if it beats the current one. */
export function recordPhonationPB(seconds: number): boolean {
  if (seconds <= profile.value.bests.phonationSec) return false;
  commit({ ...profile.value, bests: { ...profile.value.bests, phonationSec: seconds } });
  return true;
}

/** Records a new plosive-timing-accuracy PB (0-100), if it beats the current one. */
export function recordPlosiveAccuracyPB(pct: number): boolean {
  if (pct <= profile.value.bests.plosiveAccuracy) return false;
  commit({ ...profile.value, bests: { ...profile.value.bests, plosiveAccuracy: pct } });
  return true;
}

/** Records the fastest tempo a card has been cleared at (Good/Easy grade). */
export function recordWpmPB(cardId: string, wpm: number): boolean {
  const current = profile.value.bests.wpmByCard[cardId] ?? 0;
  if (wpm <= current) return false;
  commit({ ...profile.value, bests: { ...profile.value.bests, wpmByCard: { ...profile.value.bests.wpmByCard, [cardId]: wpm } } });
  return true;
}

/** Awards Clarity the instant it's earned (per drill/item), rather than
    batching it all to session end — so exiting the Booth mid-session never
    discards progress already made. */
export function awardClarity(amount: number): void {
  commit({ ...profile.value, clarity: profile.value.clarity + amount });
}

export const rank = computed(() => rankFor(profile.value.clarity));
export const streakPreview = computed(() => previewStreak(profile.value, dayStr()));
export const weeklyRing = computed(() => weeklyClosedCount(profile.value, dayStr()));

export function resetProfile(): void {
  const next = defaultProfile();
  profile.value = next;
  saveProfile(next);
}

export function pauseStreak(days: number): void {
  commit(pauseTraining(profile.value, dayStr(), days));
}

export function setTheme(theme: ThemeSetting): void {
  setThemePref(theme);
  commit({ ...profile.value, settings: { ...profile.value.settings, theme } });
}

export function setSoundSetting(key: "master" | "metronome" | "sfx" | "voice", value: boolean): void {
  commit({ ...profile.value, settings: { ...profile.value.settings, sound: { ...profile.value.settings.sound, [key]: value } } });
}

export function setMicEnabled(value: boolean): void {
  commit({ ...profile.value, settings: { ...profile.value.settings, mic: value } });
}

export function setTranscribeCoach(value: boolean): void {
  commit({ ...profile.value, settings: { ...profile.value.settings, transcribeCoach: value } });
}

export function setIdentityLangs(langs: Lang[]): void {
  const primaryLang = langs.includes(profile.value.identity.primaryLang) ? profile.value.identity.primaryLang : (langs[0] ?? "en");
  commit({ ...profile.value, identity: { ...profile.value.identity, langs, primaryLang } });
}

export function setIdentityGoal(goal: Profile["identity"]["goal"]): void {
  commit({ ...profile.value, identity: { ...profile.value.identity, goal } });
}

export function setIdentityWhen(when: Profile["identity"]["when"]): void {
  commit({ ...profile.value, identity: { ...profile.value.identity, when } });
}

export function markOnboarded(): void {
  commit({ ...profile.value, seen: { ...profile.value.seen, onboarded: true } });
}

export function setWeekTarget(target: 3 | 5 | 7): void {
  commit({ ...profile.value, week: { ...profile.value.week, target } });
}

export function setNewPerDay(n: number): void {
  commit({ ...profile.value, settings: { ...profile.value.settings, newPerDay: n } });
}

export function setReviewCap(n: number): void {
  commit({ ...profile.value, settings: { ...profile.value.settings, reviewCap: n } });
}

export function setSessionLength(v: Profile["settings"]["sessionLength"]): void {
  commit({ ...profile.value, settings: { ...profile.value.settings, sessionLength: v } });
}

/** One-shot UI nudges (e.g. "has the mic-consent panel been shown before")
    that shouldn't nag every session but aren't worth a dedicated field. */
export function hasSeenTip(tip: string): boolean {
  return profile.value.seen.tips.includes(tip);
}

export function markTipSeen(tip: string): void {
  if (hasSeenTip(tip)) return;
  commit({ ...profile.value, seen: { ...profile.value.seen, tips: [...profile.value.seen.tips, tip] } });
}

const LOG_MAX_ENTRIES = 60;

/** Appends one finished session to the log (capped at the most recent 60),
    for the Logbook and for re-opening a past Session Card. */
export function appendSessionLog(entry: Profile["log"][number]): void {
  const log = [entry, ...profile.value.log].slice(0, LOG_MAX_ENTRIES);
  commit({ ...profile.value, log });
}

export type CompleteSessionInput = {
  closesRing: boolean;
  lang?: Lang;
  clarityAwarded: number;
  secondsSpent: number;
  ctx?: PlaqueContext;
};

/** End-of-session wrap-up: streak/ring advance, plaque sweep, rank check.
    Per-item Clarity/SRS updates already happened via awardClarity/
    gradeSessionCard as they were earned — `clarityAwarded` here is just the
    session-complete bonus (see CLARITY.sessionComplete), not the total. */
export function completeSession(input: CompleteSessionInput): Reward[] {
  const today = dayStr();
  const before = rankFor(profile.value.clarity);

  let next: Profile = {
    ...profile.value,
    clarity: profile.value.clarity + input.clarityAwarded,
    seconds: profile.value.seconds + input.secondsSpent,
    sessions: profile.value.sessions + 1,
  };

  const touched = touchDay(next, today, { closesRing: input.closesRing, lang: input.lang });
  next = touched.profile;

  const swept = sweepPlaques(next, today, input.ctx);
  next = swept.profile;

  const after = rankFor(next.clarity);

  const rewards: Reward[] = [clarityReward(input.clarityAwarded)];
  for (const ev of touched.events) {
    const r = rewardFromStreakEvent(ev);
    if (r) rewards.push(r);
  }
  for (const ev of swept.events) rewards.push(rewardFromPlaque(ev));
  const rankReward = rewardFromRankChange(before, after);
  if (rankReward) rewards.push(rankReward);

  commit(next);
  return rewards;
}
